/**
 * PRD hard stop 15: while NotaVera is not accredited (API ACCREDITED=false), every screen — sign-in included — carries
 * "DEMO — NOT A NOTARIAL ACT". Fail-safe: the banner shows unless the API says, in so many words, that it is accredited.
 */
import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { api } from '../../lib/api';

export interface AppInfo {
  product: string;
  accredited: boolean;
  banner: string | null;
  modules: string[];
  /** Demo stack only: invite links and codes are kept for Notary › Demo inbox (no SMTP). */
  demoInbox?: boolean;
}

export const DEMO_TEXT = 'DEMO — NOT A NOTARIAL ACT';

/** Pure: show the banner unless the server explicitly reports accredited === true. */
export const showDemoBanner = (info: Partial<AppInfo> | null | undefined): boolean => info?.accredited !== true;

export function useAppInfo() {
  return useQuery({ queryKey: ['app-info'], queryFn: () => api<AppInfo>('/app-info', { anonymous: true }), staleTime: 10 * 60_000, retry: 1 });
}

export function DemoBanner() {
  const info = useAppInfo();
  if (!showDemoBanner(info.data)) return null;
  return (
    <div role="note" aria-label="Demo mode" className="flex items-center justify-center gap-2 bg-danger px-4 py-1.5 text-center text-xs font-bold tracking-wide text-white dark:text-paper sm:text-sm">
      <TriangleAlert aria-hidden className="h-4 w-4 shrink-0" />
      <span>
        {info.data?.banner ?? DEMO_TEXT}
        <span className="hidden font-semibold sm:inline"> · Demo / pilot built to the Supreme Court Rules on Electronic Notarization. Nothing done here is a notarial act.</span>
      </span>
    </div>
  );
}
